/**
 * İzin bakiye hesaplamaları — KARMA KAYNAK uyumlu.
 *
 * YillikBakiye: işe başlamadan kazanılan + BOSS'un eklediği başlangıç bakiyesi
 * (yillikIzinBaslangicBakiye) - şimdiye dek kullanılan
 */

import { collection, getDocs, query, where } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import {
  izinleriGetir,
  yillikKesilen, aylikKesilen,
  type IzinTalebi,
} from './izin-service'

// ═══════════════════════════════════════════════════════════════
// YILLIK BAKİYE
// ═══════════════════════════════════════════════════════════════

export interface YillikBakiye {
  kazanimSayisi: number       // tamamlanmış 6 aylık periyot sayısı
  kazanilan: number           // sistem tarafından (6 ay × 7)
  baslangicBakiye: number     // BOSS'un manuel girdiği başlangıç
  toplamHak: number           // kazanilan + baslangicBakiye
  kullanilan: number
  kalan: number
  sonrakiKazanimTarihi: string
  sonrakiKazanimaGun: number
}

function tamamlanmis6AyPeriodu(iseBaslama: Date, bugun: Date): {
  sayim: number
  sonrakiTarih: Date
} {
  let sayim = 0
  const hedef = new Date(iseBaslama)
  hedef.setMonth(hedef.getMonth() + 6)
  while (hedef.getTime() <= bugun.getTime()) {
    sayim++
    hedef.setMonth(hedef.getMonth() + 6)
  }
  return { sayim, sonrakiTarih: hedef }
}

export async function yillikBakiye(
  personelId: string,
  iseBaslamaTarihi: string,
  baslangicBakiye: number = 0
): Promise<YillikBakiye> {
  const baslangic = new Date(iseBaslamaTarihi + 'T00:00:00')
  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)

  const { sayim, sonrakiTarih } = tamamlanmis6AyPeriodu(baslangic, bugun)
  const kazanilan = sayim * 7
  const toplamHak = kazanilan + baslangicBakiye

  const tumIzinler = await izinleriGetir({
    personelId,
    durumlar: ['onaylandi'],
  })
  const yillikKullanilan = tumIzinler.reduce(
    (acc, i) => acc + yillikKesilen(i),
    0
  )
  const kalan = Math.max(0, toplamHak - yillikKullanilan)

  const fark = sonrakiTarih.getTime() - bugun.getTime()
  const sonrakiKazanimaGun = Math.ceil(fark / (1000 * 60 * 60 * 24))

  return {
    kazanimSayisi: sayim,
    kazanilan,
    baslangicBakiye,
    toplamHak,
    kullanilan: yillikKullanilan,
    kalan,
    sonrakiKazanimTarihi: sonrakiTarih.toISOString().substring(0, 10),
    sonrakiKazanimaGun,
  }
}

// ═══════════════════════════════════════════════════════════════
// AYLIK BAKİYE
// ═══════════════════════════════════════════════════════════════

export interface AylikBakiye {
  yil: number
  ay: number
  vardiyaSayisi: number
  hak: number
  kullanilan: number
  kalan: number
}

async function ayinVardiyaSayisi(
  personelId: string,
  yil: number,
  ay: number
): Promise<number> {
  const db = getFirestoreDb()
  const baslangic = new Date(yil, ay - 1, 1)
  const bitis = new Date(yil, ay, 1)
  const q = query(
    collection(db, 'kayitlar'),
    where('personelId', '==', personelId),
    where('tipi', '==', 'giris'),
    where('zaman', '>=', baslangic),
    where('zaman', '<', bitis)
  )
  try {
    const snap = await getDocs(q)
    const gunler = new Set<string>()
    snap.docs.forEach((d) => {
      const ts = d.data().zaman as { toDate: () => Date }
      const gun = ts.toDate().toISOString().substring(0, 10)
      gunler.add(gun)
    })
    return gunler.size
  } catch (err) {
    console.warn('Vardiya sayısı hesaplanamadı:', err)
    return 0
  }
}

export async function aylikBakiye(
  personelId: string,
  yil: number,
  ay: number
): Promise<AylikBakiye> {
  const vardiyaSayisi = await ayinVardiyaSayisi(personelId, yil, ay)
  const hak = Math.floor(vardiyaSayisi / 6)

  const tumIzinler = await izinleriGetir({
    personelId,
    durumlar: ['onaylandi'],
  })
  const ayBaslangic = `${yil}-${String(ay).padStart(2, '0')}-01`
  const ayBitis = `${yil}-${String(ay).padStart(2, '0')}-31`
  const aylikKullanilan = tumIzinler
    .filter((i) => i.baslangicTarihi >= ayBaslangic && i.baslangicTarihi <= ayBitis)
    .reduce((acc, i) => acc + aylikKesilen(i), 0)

  const kalan = hak - aylikKullanilan

  return {
    yil,
    ay,
    vardiyaSayisi,
    hak,
    kullanilan: aylikKullanilan,
    kalan,
  }
}

// ═══════════════════════════════════════════════════════════════
// AGGREGATE
// ═══════════════════════════════════════════════════════════════

export interface IzinOzeti {
  yillik: YillikBakiye | null
  aylik: AylikBakiye
  bekleyenTalep: IzinTalebi[]
  sonOnayli: IzinTalebi[]
}

export async function personelIzinOzeti(
  personelId: string,
  iseBaslamaTarihi: string | null,
  isFulltime: boolean,
  baslangicBakiye: number = 0,
  yil?: number,
  ay?: number
): Promise<IzinOzeti> {
  const bugun = new Date()
  const hedefYil = yil ?? bugun.getFullYear()
  const hedefAy = ay ?? bugun.getMonth() + 1

  const [yillik, aylik, bekleyen, onayli] = await Promise.all([
    isFulltime && iseBaslamaTarihi
      ? yillikBakiye(personelId, iseBaslamaTarihi, baslangicBakiye)
      : Promise.resolve(null),
    aylikBakiye(personelId, hedefYil, hedefAy),
    izinleriGetir({ personelId, durumlar: ['bekliyor'] }),
    izinleriGetir({ personelId, durumlar: ['onaylandi'] }).then((arr) =>
      arr.slice(0, 5)
    ),
  ])

  return {
    yillik,
    aylik,
    bekleyenTalep: bekleyen,
    sonOnayli: onayli,
  }
}
